import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';
import { ApiRequestError } from '@/lib/apiClient';
import { applyFieldErrors } from '@/lib/forms';
import type { InstituteAdmin } from '@/types/institute';
import { institutesApi } from '../api/institutes.api';
import {
  addAdminSchema,
  EMPTY_ADD_ADMIN_FORM,
  type AddAdminFormValues,
  type AddAdminPayload,
} from '../schemas/admin.schema';

export interface AddAdminDialogProps {
  instituteUuid: string;
  instituteName: string;
  isOpen: boolean;
  onClose: () => void;
  /** Gets the new administrator back, so the panel can confirm it by name. */
  onAdded: (admin: InstituteAdmin) => void;
}

/**
 * Gives an institute another administrator.
 *
 * No password field, on purpose: the server emails them a one-time link and
 * they choose their own, so nobody on our side ever knows it — exactly as for
 * the institute's first administrator.
 */
export function AddAdminDialog({
  instituteUuid,
  instituteName,
  isOpen,
  onClose,
  onAdded,
}: AddAdminDialogProps) {
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<AddAdminFormValues, unknown, AddAdminPayload>({
    resolver: zodResolver(addAdminSchema),
    defaultValues: EMPTY_ADD_ADMIN_FORM,
  });
  useLiveFormErrors(form);
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = form;

  // Emptied every time it opens, so one person's details never sit under the next.
  useEffect(() => {
    if (!isOpen) return;
    setFormError(null);
    reset(EMPTY_ADD_ADMIN_FORM);
  }, [isOpen, reset]);

  const onSubmit = handleSubmit(async (payload) => {
    setFormError(null);
    try {
      onAdded(await institutesApi.addAdmin(instituteUuid, payload));
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        // An address or number already in use lands on its own field.
        applyFieldErrors(setError, caught);
        setFormError(caught.message);
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add an administrator"
      description={`They get the same full access to ${instituteName} as its other administrators, and an email with a link to set their own password.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={onSubmit} isLoading={isSubmitting}>
            {isSubmitting ? 'Adding…' : 'Add administrator'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {formError && (
          <Alert tone="critical" title="Could not add this administrator">
            {formError}
          </Alert>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="First name"
            autoComplete="off"
            error={errors.firstName?.message}
            {...register('firstName')}
          />
          <Input
            label="Last name"
            autoComplete="off"
            error={errors.lastName?.message}
            {...register('lastName')}
          />
        </div>

        <Input
          label="Email"
          type="email"
          autoComplete="off"
          hint="They sign in with this address. It cannot already be in use on another account."
          error={errors.email?.message}
          {...register('email')}
        />

        <PhoneInput label="Mobile" error={errors.phone?.message} {...register('phone')} />
      </form>
    </Modal>
  );
}
